"use client";

// The deposit modal's rendering half. Every value it draws comes from
// `RelayWizardContext`'s bundle (published by `RelayWizardEngine`, which lives
// ABOVE the shell -- see RelayWizardStateHost.tsx) plus the shell context
// (theme / viewport / close) and `<RelayDeposit>`'s own props; this file owns no
// state and calls no data hook, which is exactly what lets radix `Dialog` and
// vaul `Drawer` swap under it LIVE at the 768px breakpoint without losing the
// user's place. Transient DOM state (input focus, scroll offset) still resets on
// such a swap -- the nodes are genuinely new -- but the typed amount, selected
// origin/target, the fetched deposit address and in-flight tx tracking do not.
//
// 2026-07-31 (user decision): this is ONE screen, not a 2-step wizard. The
// asset selectors and the QR/address live in a single scroll column, so there
// is no step state, no stepper, no Continue/Back, and no per-deposit
// full-screen waiting/success step. Manual sends are observed indirectly via
// the orders SSE + 1s REST re-fetch and rendered in a separate status card.
// See DepositPanel.tsx (which replaced
// steps/SourceStep + steps/ExecuteStep) for the full rationale.
//
// Layout/markup ported from relay-protocol
// packages/relay-widget/src/components/RelayDeposit.tsx (v0.1.0): same section
// order and recovery aggregation, hosted inside dapp-ui's modal shell.
// Differences from the source widget, all deliberate:
//
//   * wallet capabilities arrive as injected props (RelayWalletProps) rather
//     than wagmi hooks, and are handed to useDepositAddress as `wallet`;
//   * the full `OrdersList` is gone, while its SSE invalidation hook is shared
//     with the inline deposit-arrival monitor and recovery panel;
//   * the theme comes from the shell context (`data-theme` on `.rd-root`),
//     which has no 'auto' option — hosts pick light or dark explicitly.
import * as React from "react";

import { TrackingBoundary } from "../../../lib/sentry/index";
import { useRelayDepositContext } from "../providers/relay-deposit-provider";
import { useRelayShellContext } from "../providers/relay-shell-provider";
import {
  useRelayWizardBundle,
  type RelayDepositView,
} from "../providers/relay-wizard-provider";
import { DepositPanel } from "./DepositPanel";
import { DepositAddressSkeleton } from "./DepositStepView";
import { HistoryEmptyState, OrdersList } from "./OrdersList";
import { RecoverFunds } from "./RecoverFunds";
import { ShellContent } from "./shell/ResponsiveShell";
import "../styles/component.css";

function DepositTabs({
  id,
  activeView,
  recoverableCount,
  onChange,
}: {
  id: string;
  activeView: RelayDepositView;
  recoverableCount: number;
  onChange: (view: RelayDepositView) => void;
}) {
  const tabs: readonly RelayDepositView[] = ["deposit", "history", "recover"];

  return (
    <div
      className="rd-deposit-tabs"
      role="tablist"
      aria-label="Deposit navigation"
      data-tab-count={tabs.length}
      data-active-index={tabs.indexOf(activeView)}
    >
      {tabs.map((view) => {
        const label = view === "deposit" ? "Deposit" : view === "history" ? "History" : "Recover";
        return (
          <button
            key={view}
            type="button"
            id={`${id}-${view}-tab`}
            className="rd-deposit-tab"
            role="tab"
            aria-selected={activeView === view}
            aria-controls={`${id}-${view}-panel`}
            tabIndex={activeView === view ? 0 : -1}
            data-active={activeView === view ? "true" : "false"}
            data-track={`${view}_tab`}
            onClick={() => onChange(view)}
            onKeyDown={(event) => {
              if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
              event.preventDefault();
              const currentIndex = tabs.indexOf(view);
              const nextIndex =
                event.key === "Home"
                  ? 0
                  : event.key === "End"
                    ? tabs.length - 1
                    : (currentIndex + (event.key === "ArrowRight" ? 1 : -1) + tabs.length) % tabs.length;
              const nextView = tabs[nextIndex];
              onChange(nextView);
              document.getElementById(`${id}-${nextView}-tab`)?.focus();
            }}
          >
            <span>{label}</span>
            {view === "recover" && recoverableCount > 0 && (
              <span className="rd-recovery-badge" data-testid="recovery-badge">
                {recoverableCount}
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

export interface RelayDepositContentProps {
  /** Extra class name(s) for the dialog/drawer content element. Falls back to
   * `<RelayDeposit>`'s own `className`. */
  className?: string;
}

/**
 * `<RelayDeposit.Content>` — the modal/drawer surface. The deposit body is a
 * child of `ShellContent`, i.e. of radix's `Dialog.Portal` / vaul's
 * `Drawer.Portal`, which render nothing while closed. The state it renders is
 * NOT its own any more: `<RelayDeposit>` mounts the wizard engine above the
 * shell for exactly as long as the modal is open, so "no request before open"
 * and "everything resets on close" still hold -- while a breakpoint crossing
 * mid-flow now only rebuilds this DOM, not the flow.
 */
export function RelayDepositContent({ className }: RelayDepositContentProps) {
  const props = useRelayDepositContext();

  return (
    <TrackingBoundary feature="relay">
      <ShellContent
        ariaTitle="Deposit"
        className={["rd-deposit-shell", className ?? props.className].filter(Boolean).join(" ")}
      >
        <RelayDepositBody />
      </ShellContent>
    </TrackingBoundary>
  );
}

function RelayDepositBody() {
  const props = useRelayDepositContext();
  const { theme } = useRelayShellContext();
  const wizard = useRelayWizardBundle();
  const tabsId = React.useId();

  // No bundle means the engine isn't mounted, i.e. the modal is closed -- the
  // portal above renders nothing in that state anyway, so this is only ever hit
  // for the single pre-paint render of an opening modal (the engine publishes in
  // a layout effect that lands before this subtree's own; see
  // useRelayWizardBundle). Rendering nothing keeps that frame empty rather than
  // half-built.
  if (!wizard) return null;

  const {
    activeView,
    setActiveView,
    recoveryOpen,
    resolvedApiBaseUrl,
    wallet,
    catalog,
    catalogLoading,
    catalogError,
    origin,
    setOrigin,
    amount,
    setAmount,
    recipient,
    setRecipient,
    recipientValid,
    balance,
    balanceLoading,
    insufficientBalance,
    depositAddress,
    depositAddressLoading,
    depositAddressError,
    arrivalStatus,
    quote,
    quoteLoading,
    quoteError,
    canDeposit,
    deposit,
    depositState,
    targetOptions,
    selectedTarget,
    setSelectedTarget,
    showAmountInput,
    minNotice,
    showRecipientInput,
    showTargetSelector,
    showOneClick,
    showConnectPrompt,
    showRecipientHint,
    canRecover,
    recoverableCount,
    recoveryTargetsLoading,
    recoveryTargets,
    recoveryOrders,
    historyOrdersLoading,
    historyOrdersError,
    refreshRecovery,
    trustedFactories,
    amountHint,
    amountSmallestUnits,
    onMax,
  } = wizard;

  return (
    <div
      className="rd-root rd-wizard"
      data-theme={theme}
      data-rd-scroll-area
    >
      <DepositTabs
        id={tabsId}
        activeView={activeView}
        recoverableCount={recoverableCount}
        onChange={setActiveView}
      />

      {recoveryOpen && (
        <div
          className="rd-deposit-tab-panel"
          id={`${tabsId}-recover-panel`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-recover-tab`}
        >
          {canRecover ? (
            <RecoverFunds
              client={props.client}
              apiBaseUrl={resolvedApiBaseUrl}
              recipient={recipient}
              target={selectedTarget?.symbol}
              wallet={wallet}
              trustedFactories={trustedFactories}
              targets={(catalog?.targets.length ?? 0) > 0 ? recoveryTargets : undefined}
              targetsLoading={recoveryTargetsLoading}
              orders={recoveryOrders}
              onRecovered={refreshRecovery}
              onRecoverySuccess={props.onRecoverySuccess}
              onError={props.onError}
              showTitle={false}
            />
          ) : (
            <div
              className="rd-recover-empty"
              role="status"
              data-variant="unavailable"
              data-testid="rd-recovery-unavailable-note"
            >
              <span className="rd-recover-empty-icon" aria-hidden="true">
                <svg width="32" height="32" viewBox="0 0 24 24" fill="none" focusable="false">
                  <rect
                    x="5"
                    y="10"
                    width="14"
                    height="10"
                    rx="2.5"
                    stroke="currentColor"
                    strokeWidth="1.7"
                  />
                  <path
                    d="M8 10V7.5a4 4 0 0 1 8 0V10"
                    stroke="currentColor"
                    strokeWidth="1.7"
                    strokeLinecap="round"
                  />
                  <circle cx="12" cy="15" r="1.2" fill="currentColor" />
                </svg>
              </span>
              <h3 className="rd-recover-empty-title">Recovery unavailable</h3>
              <p className="rd-recover-empty-copy">
                Connect a wallet that supports recovery transactions to continue.
              </p>
            </div>
          )}
        </div>
      )}

      {activeView === "history" && (
        <div
          className="rd-deposit-tab-panel"
          id={`${tabsId}-history-panel`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-history-tab`}
        >
          {recipientValid && recipient.trim() ? (
            <OrdersList
              orders={recoveryOrders}
              isLoading={catalogLoading || historyOrdersLoading}
              error={catalogError ?? historyOrdersError}
              catalog={catalog}
              showTitle={false}
            />
          ) : (
            <HistoryEmptyState requiresIdentity />
          )}
        </div>
      )}

      {activeView === "deposit" && (
        <div
          className="rd-deposit-tab-panel"
          id={`${tabsId}-deposit-panel`}
          role="tabpanel"
          aria-labelledby={`${tabsId}-deposit-tab`}
        >

      {/* Catalog skeleton: mirrors the settled panel's real block order and
          dimensions. Footer/details below render in both states, so only the
          data-driven body is represented here. */}
      {catalogLoading && (
        <div className="rd-skeleton-form" data-testid="rd-source-skeleton" aria-hidden="true">
          <DepositAddressSkeleton />

          {showConnectPrompt && props.onRequestConnect && <span className="rd-skeleton rd-skeleton-cta" />}

          <section className="rd-stack">
            <div className="rd-field">
              <span className="rd-skeleton rd-skeleton-line" style={{ width: "18%" }} />
              <div className="rd-field-row">
                <span className="rd-skeleton rd-skeleton-field" />
                <span className="rd-skeleton rd-skeleton-field" />
              </div>
            </div>

            {showAmountInput && (
              <div className="rd-field">
                <span className="rd-skeleton rd-skeleton-line" style={{ width: "34%" }} />
                <span className="rd-skeleton rd-skeleton-field" />
              </div>
            )}
          </section>

          <div className="rd-field">
            <span className="rd-skeleton rd-skeleton-line" style={{ width: "24%" }} />
            <span className="rd-skeleton rd-skeleton-field" />
            {(showRecipientInput || recipient.trim().length > 0) && (
              <>
                <span className="rd-skeleton rd-skeleton-line" style={{ width: "58%", marginTop: "0.5rem" }} />
                <span className="rd-skeleton rd-skeleton-field" />
                {showRecipientInput && <span className="rd-skeleton rd-skeleton-line" style={{ width: "82%" }} />}
              </>
            )}
          </div>

          <span className="rd-skeleton rd-skeleton-notice" />
        </div>
      )}
      {catalogLoading && (
        <p className="rd-sr-only" role="status">
          Loading deposit options...
        </p>
      )}

      {catalogError && (
        <div className="rd-callout rd-callout-danger">
          Failed to load configuration: {catalogError.message}
        </div>
      )}

      {/* (The connect-prompt card that used to sit here left with the
          2026-07-31 "A안 · QR 퍼스트" layout: the disconnected state is now shown
          where it actually bites -- a locked card IN THE QR SLOT plus the
          `Connect Wallet` CTA, rendered by <DepositPanel> via `locked`. A card
          above the form saying the same thing was the second copy of it, and it
          pushed the (empty) address slot even further down.) */}

      {showRecipientHint && (
        <div className="rd-callout rd-callout-muted">
          {showRecipientInput
            ? "Connect a wallet or provide a recipient address to fetch your deposit address."
            : "Connect a wallet to fetch your deposit address."}
        </div>
      )}

      {/* The whole body, one screen, QR first: context line + `change`
          disclosure, optional amount + quote, the QR/address block (or the
          locked card when there is no identity yet), the receive strip and the
          send-rules checklist. `<DepositPanel>` renders the address section
          itself (spinner line while loading, error callout on failure), so the
          address disappearing mid-flow -- an origin/target change, a failed
          refetch -- is explained in place. */}
      {catalog && origin && (
        <DepositPanel
          catalog={catalog}
          origin={origin}
          onChangeOrigin={setOrigin}
          amount={amount}
          onChangeAmount={setAmount}
          showAmountInput={showAmountInput}
          // Passed in BOTH modes now: it feeds the checklist card, which states
          // the effective minimum whether or not an amount input is shown (the
          // amount input's own inline below-min/above-max advisory is
          // `amountHint`, and is unrelated).
          minNotice={minNotice}
          amountHint={amountHint}
          onMax={onMax}
          recipient={recipient}
          onChangeRecipient={setRecipient}
          showRecipientInput={showRecipientInput}
          recipientValid={recipientValid}
          balance={balance}
          balanceLoading={balanceLoading}
          insufficientBalance={insufficientBalance}
          targetOptions={targetOptions}
          selectedTarget={selectedTarget}
          onChangeTarget={setSelectedTarget}
          showTargetSelector={showTargetSelector}
          quote={quote}
          quoteLoading={quoteLoading}
          quoteError={quoteError}
          depositAddress={depositAddress}
          depositAddressLoading={depositAddressLoading}
          depositAddressError={depositAddressError}
          arrivalStatus={arrivalStatus}
          amountSmallestUnits={amountSmallestUnits}
          depositState={depositState}
          onDeposit={() => void deposit()}
          canDeposit={canDeposit && showOneClick}
          oneClickEnabled={showOneClick}
          locked={showConnectPrompt}
          onRequestConnect={props.onRequestConnect}
        />
      )}
        </div>
      )}
    </div>
  );
}
